import type {
  CancelInterventionRequest,
  CustomFormReportDocument,
  FinishInterventionRequest,
  InterventionDetailAlt,
  InterventionToEdit,
  OrderCustomFormToAdd,
  OrderCustomFormToEdit,
  PlanInterventionRequest,
  ReplanInterventionRequest,
  SendDepartureLateNotificationRequest,
  SendNotificationManuallyRequest,
  StartDepartureForInterventionRequest,
  TableResultIntervention
} from 'api/models/mainservice';
import { protectedApiClient } from 'api/support/baseApiClients';
import { getUploadHeaders } from 'api/support/helper';
import type { GeoCoordinate } from 'common/models';
import type { InterventionTableQuery } from 'pages/interventions/models';
import type { OrderDetailAlt } from 'pages/orders/models';

export default {
  getIntervention: async (orderId: string, interventionId: string) => {
    const result = await protectedApiClient.get<InterventionDetailAlt>(
      `/order/${orderId}/intervention/${interventionId}`
    );

    return result.data;
  },
  editIntervention: async (params: {
    orderId: string;
    interventionId: string;
    values: InterventionToEdit;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}`,
      params.values
    );

    return result.data;
  },
  replanIntervention: async (params: {
    orderId: string;
    interventionId: string;
    intervention: ReplanInterventionRequest;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/replan`,
      params.intervention
    );

    return result.data;
  },
  startIntervention: async (params: {
    orderId: string;
    interventionId: string;
    location?: GeoCoordinate;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/start`,
      {
        location: params.location
      }
    );

    return result.data;
  },
  startDepartureIntervention: async (params: {
    orderId: string;
    interventionId: string;
    values: StartDepartureForInterventionRequest;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/startDeparture`,
      params.values
    );

    return result.data;
  },
  sendDepartureLateIntervention: async (params: {
    orderId: string;
    interventionId: string;
    values: SendDepartureLateNotificationRequest;
  }) => {
    const result = await protectedApiClient.put<undefined>(
      `/order/${params.orderId}/intervention/${params.interventionId}/sendDepartureLateNotification`,
      params.values
    );

    return result.data;
  },
  finishIntervention: async (params: {
    orderId: string;
    interventionId: string;
    data: FinishInterventionRequest;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/finish`,
      params.data
    );

    return result.data;
  },
  confirmSignatures: async (params: {
    orderId: string;
    interventionId: string;
    location?: GeoCoordinate;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/ConfirmSignatures`,
      { location: params.location }
    );

    return result.data;
  },
  confirmIntervention: async (params: { orderId: string; interventionId: string }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/Confirm`
    );
    return result.data;
  },
  sendReportsAgain: (orderId: string, interventionId: string, email: string) => {
    return protectedApiClient.put(
      `/order/${orderId}/intervention/${interventionId}/SendReportsAgain`,
      {
        email
      }
    );
  },
  sendNotificationManually: (
    orderId: string,
    interventionId: string,
    data: SendNotificationManuallyRequest
  ) => {
    return protectedApiClient.put<undefined>(
      `/order/${orderId}/intervention/${interventionId}/SendNotificationManually`,
      data
    );
  },
  cancelIntervention: async (params: {
    orderId: string;
    interventionId: string;
    data: CancelInterventionRequest;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/Cancel`,
      params.data
    );

    return result.data;
  },
  planIntervention: async (params: { orderId: string; intervention: PlanInterventionRequest }) => {
    const result = await protectedApiClient.post<OrderDetailAlt>(
      `/v2/order/${params.orderId}/PlanIntervention`,
      params.intervention
    );

    return result.data;
  },
  getInterventions: async (params: InterventionTableQuery) => {
    const result = await protectedApiClient.get<TableResultIntervention>(`/order/intervention`, {
      params
    });

    return result.data;
  },
  addCustomForm: async (params: {
    orderId: string;
    interventionId: string;
    data: OrderCustomFormToAdd;
  }) => {
    const result = await protectedApiClient.post<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm`,
      params.data
    );

    return result.data;
  },
  editCustomForm: async (params: {
    orderId: string;
    interventionId: string;
    customFormId: string;
    data: OrderCustomFormToEdit;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm/${params.customFormId}`,
      params.data
    );

    return result.data;
  },
  deleteCustomForm: async (params: {
    orderId: string;
    interventionId: string;
    customFormId: string;
  }) => {
    const result = await protectedApiClient.delete<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm/${params.customFormId}`
    );

    return result.data;
  },
  saveCustomFormProgress: async (params: {
    orderId: string;
    interventionId: string;
    customFormId: string;
    data: string;
  }) => {
    const result = await protectedApiClient.patch<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm/${params.customFormId}/save`,
      { data: params.data }
    );

    return result.data;
  },
  submitCustomForm: (
    orderId: string,
    interventionId: string,
    customFormId: string,
    data: string
  ) => {
    return protectedApiClient.post<InterventionDetailAlt>(
      `/order/${orderId}/intervention/${interventionId}/customForm/${customFormId}`,
      { data }
    );
  },
  signInReport: async (params: {
    orderId: string;
    customFormId: string;
    interventionId: string;
    reportId: string;
    data: FormData;
  }) => {
    const result = await protectedApiClient.post<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm/${params.customFormId}/sign/${params.reportId}`,
      params.data,
      getUploadHeaders
    );

    return result.data;
  },
  getReport: async (
    orderId: string,
    interventionId: string,
    customFormId: string,
    reportId: string,
    serviceObjectId?: string
  ) => {
    const result = await protectedApiClient.get<CustomFormReportDocument>(
      serviceObjectId
        ? `/order/${orderId}/intervention/${interventionId}/customForm/${customFormId}/getReport/${reportId}/${serviceObjectId}`
        : `/order/${orderId}/intervention/${interventionId}/customForm/${customFormId}/getReport/${reportId}`
    );

    return result.data;
  },
  deleteIntervention: async (params: { orderId: string; interventionId: string }) => {
    const result = await protectedApiClient.delete<undefined>(
      `/order/${params.orderId}/intervention/${params.interventionId}`
    );

    return result.data;
  },
  resetIntervention: async (params: { orderId: string; interventionId: string }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/resetstate`
    );

    return result.data;
  },
  regenerateInterventionCustomForm: async (params: {
    orderId: string;
    interventionId: string;
    customFormId: string;
  }) => {
    const result = await protectedApiClient.put<InterventionDetailAlt>(
      `/order/${params.orderId}/intervention/${params.interventionId}/customForm/${params.customFormId}/regenerate`
    );

    return result.data;
  }
};
